import { EventEmitter } from 'events'

import { chunk } from 'lodash-es'

import { getAuthorName } from 'src/components/TranscriptViewer/ParagraphAuthor/getAuthorName'
import { getSessionTitle, TranscriptMetadata } from 'src/models/TranscriptMetadata'
import { platformHttpClient } from 'src/network'
import { TranscriptClient } from 'src/models/TranscriptClient'

export const getUniqueFilename = (prefix: string) => {
    const now = new Date()
    const timeString = now.toISOString().replace(/[:T]/g, '-').replace(/\..+/g, '')

    return `${prefix}-${timeString}`
}

type DownloadFileParams = {
    blob: Blob
    filename: string
}
export const downloadFile = ({ blob, filename }: DownloadFileParams) => {
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.download = filename
    link.href = url
    link.click()

    URL.revokeObjectURL(url)
    link.remove()
}

export const getTranscriptPlainText = (transcript: TranscriptClient, metadata: TranscriptMetadata) => {
    const lines: string[] = []
    const totalParagraphCount = transcript.getTotalParagraphCount()
    const isDiarizationSupported = transcript.isDiarizationSupported()
    const shouldSuppressSpeakerName = metadata.suppressSpeakerName
    const viewMode = metadata.viewMode ?? null

    for (let i = 0; i < totalParagraphCount; i++) {
        const paragraph = transcript.getParagraphByIndex(i)
        const label = getAuthorName({
            author: transcript.getSpeakerById(paragraph.speakerId),
            shouldSuppressSpeakerName,
            isDiarizationSupported,
            viewMode,
        })
        const prefix = label || 'Speaker'

        lines.push(`${prefix}: ${paragraph.content.map(({ text }) => text).join('')}`)
        lines.push('')
    }

    return lines.join('\n')
}

export const downloadTranscriptFile = (transcript: TranscriptClient, metadata: TranscriptMetadata) => {
    downloadFile({
        blob: new Blob([getTranscriptPlainText(transcript, metadata)], { type: 'text/plain' }),
        filename: `${getSessionTitle(metadata)}.txt`,
    })
}

const getBaseUrl = (url: string) => new URL(url).origin + new URL(url).pathname.substring(0, new URL(url).pathname.lastIndexOf('/') + 1)

const fetchPlaylist = async (m3u8Url: string): Promise<string[]> => {
    const playlistResponse = await fetch(m3u8Url, {
        credentials: 'include',
    })
    if (!playlistResponse.ok) {
        throw new Error(`Failed to fetch playlist from ${m3u8Url}: ${playlistResponse.statusText}`)
    }

    const playlistContent = await playlistResponse.text()
    const baseUrl = getBaseUrl(m3u8Url)

    const lines = playlistContent.split('\n')
    const segmentUrls: string[] = []

    for (const line of lines) {
        const trimmedLine = line.trim()
        if (trimmedLine.startsWith('#') || trimmedLine === '') {
            continue
        }

        // if it's a relative URL, make it absolute
        const segmentUrl = trimmedLine.startsWith('http') ? trimmedLine : new URL(trimmedLine, baseUrl).href
        segmentUrls.push(segmentUrl)
    }

    return segmentUrls
}

const fetchSegment = async (url: string): Promise<Blob> => {
    const response = await fetch(url, {
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Failed to download segment from ${url}: ${response.statusText}`)
    }

    return response.blob()
}

type DownloadMediaFileEvents = {
    progress: [number]
    error: [Error]
}
type DownloadHLSStreamParams = {
    m3u8Url: string
    filename: string
    progressEmitter: EventEmitter<DownloadMediaFileEvents>
}

const downloadHLSStream = async ({ m3u8Url, filename, progressEmitter }: DownloadHLSStreamParams): Promise<void> => {
    try {
        const segmentUrls = await fetchPlaylist(m3u8Url)
        if (segmentUrls.length === 0) {
            throw new Error('No segments found in playlist')
        }

        const chunkSize = 10
        const chunks = chunk(segmentUrls, chunkSize)

        const blobParts: Blob[] = []
        let fetchedBlobsCount = 0

        for (const segmentsChunk of chunks) {
            blobParts.push(
                ...(await Promise.all(
                    segmentsChunk.map(async (url) => {
                        const segmentBlob = await fetchSegment(url)
                        fetchedBlobsCount += 1
                        progressEmitter.emit('progress', fetchedBlobsCount / segmentUrls.length)

                        return segmentBlob
                    }),
                )),
            )
        }

        const blob = new Blob(blobParts, { type: 'video/mp4' })
        downloadFile({ blob, filename })
    } catch (error) {
        progressEmitter.emit('error', error as Error)
    }
}

type DownloadMediaFileResult =
    | {
          type: 'hls'
          events: EventEmitter<DownloadMediaFileEvents>
      }
    | {
          type: 'mp4'
      }
    | {
          type: 'error'
      }

export const downloadMediaFile = async (transcriptJobId: string): Promise<DownloadMediaFileResult> => {
    try {
        const { mediaUrl, isHls } = await platformHttpClient.getMedia(transcriptJobId)
        const filename = `${getUniqueFilename('media')}.mp4`

        if (isHls) {
            const highQualityMediaUrl = getBaseUrl(mediaUrl) + 'high/media.m3u8'
            const progressEmitter = new EventEmitter<DownloadMediaFileEvents>()
            downloadHLSStream({ m3u8Url: highQualityMediaUrl, filename, progressEmitter })

            return {
                type: 'hls',
                events: progressEmitter,
            }
        }

        const link = document.createElement('a')
        link.download = filename
        link.href = mediaUrl
        link.click()
        link.remove()

        return {
            type: 'mp4',
        }
    } catch (error) {
        console.error('Error downloading media file:', error)

        return {
            type: 'error',
        }
    }
}
